import { boundingBox } from '../../common/pdfReport';

/**
 * Describes a coastal squeeze analysis as a document, for CSqueeze and EUSCoast alike.
 * The two modules run the same analysis over different land cover collections, and their result
 * pages are near-duplicates - the reason {@link module:LAND_COVER_TRANSECTS} and
 * {@link buildLandCoverGeoJson} are already shared. Keeping the report here too means a fix lands
 * in both instead of in whichever one was open at the time, which is the failure this codebase has
 * had before.
 * What the analysis produces is a squeeze classification per transect: whether the coastal habitat
 * has room to retreat inland or is pinned against something that will not move. The report is
 * built around that, with the classification first and the transects listed under it.
 * @module LAND_COVER_PDF_REPORT
 */

/**
 * @constant TRANSECT_TABLE_LIMIT
 * @description How many transects are listed one by one, for the same reason the coastline report
 * caps its own table: past this the listing stops being read and starts being scrolled.
 * @memberof module:LAND_COVER_PDF_REPORT
 */
export const TRANSECT_TABLE_LIMIT = 60;

/**
 * @function squeezeEntries
 * @description The analysed transects as a sorted list, from the object keyed by transect number.
 * Sorted numerically, because the keys are numbers held as strings and the default ordering would
 * put transect 10 before transect 2.
 * @param {Object} [attributes] - `transectsSqueeze`, keyed by transect number.
 * @memberof module:LAND_COVER_PDF_REPORT
 * @returns {Array<Object>} `{ transect, ...attributes }` entries.
 */
export const squeezeEntries = (attributes) =>
  Object.entries(attributes || {})
    .map(([transect, values]) => ({ transect: Number(transect), ...(values || {}) }))
    .filter((entry) => Number.isFinite(entry.transect))
    .sort((a, b) => a.transect - b.transect);

/**
 * @function squeezeClassTable
 * @description How many transects fell in each squeeze class, with the share of the total.
 * @param {Array<Object>} entries - From {@link squeezeEntries}.
 * @param {Object} text - `{ columns: { className, count, share } }`, already translated.
 * @memberof module:LAND_COVER_PDF_REPORT
 * @returns {Object} A table for {@link renderReport}.
 */
export const squeezeClassTable = (entries, text) => {
  const counts = new Map();
  (entries || []).forEach((entry) => {
    const label = entry.squeeze_label || entry.squeeze_class;
    if (label === null || label === undefined || label === '') return;
    counts.set(label, (counts.get(label) || 0) + 1);
  });

  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);
  return {
    columns: [text.columns.className, text.columns.count, text.columns.share],
    rows: [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => [label, count, total ? `${((count / total) * 100).toFixed(1)}%` : null]),
  };
};

/**
 * @function squeezeTransectTable
 * @description The transects listed one by one with their class and the squeeze percentage.
 * @param {Array<Object>} entries - From {@link squeezeEntries}.
 * @param {Object} text - `{ columns: { transect, className, percent } }`, already translated.
 * @memberof module:LAND_COVER_PDF_REPORT
 * @returns {Object} A table for {@link renderReport}.
 */
export const squeezeTransectTable = (entries, text) => ({
  columns: [text.columns.transect, text.columns.className, text.columns.percent],
  align: ['left', 'left', 'right'],
  rows: (entries || [])
    .slice(0, TRANSECT_TABLE_LIMIT)
    .map((entry) => [
      entry.transect,
      entry.squeeze_label || entry.squeeze_class || null,
      Number.isFinite(Number(entry.squeeze_percent)) && entry.squeeze_percent !== null
        ? `${Number(entry.squeeze_percent).toFixed(1)}%`
        : null,
    ]),
});

/**
 * @function landCoverTotalsTable
 * @description The land cover area per period, summed over every transect.
 * This is the analysis's headline: how much of each cover type the coastal strip held in each
 * period, and therefore what is being squeezed out of it. Per-transect rows would be hundreds of
 * lines for the same information, so they are summed - the per-transect detail is what the CSV
 * export is for.
 * Each transect's rows arrive with their own header row naming the cover types, in the language the
 * analysis ran in; the header is read from the first transect and the rest are accumulated by the
 * period label in their first column.
 * @param {Object} [calcs] - `transectsCalcs`, keyed by transect number, each a list of rows whose
 *  first row is the header.
 * @param {Object} text - `{ columns: { period } }`, already translated.
 * @memberof module:LAND_COVER_PDF_REPORT
 * @returns {Object} A table for {@link renderReport}, empty when nothing has been analysed.
 */
export const landCoverTotalsTable = (calcs, text) => {
  const perTransect = Object.values(calcs || {}).filter((rows) => Array.isArray(rows) && rows.length > 1);
  if (!perTransect.length) return { columns: [], rows: [] };

  const header = perTransect[0][0] || [];
  const covers = header.slice(1);
  const totals = new Map();
  const order = [];

  perTransect.forEach((rows) => {
    rows.slice(1).forEach((row) => {
      const period = row?.[0];
      if (period === null || period === undefined || period === '') return;
      if (!totals.has(period)) {
        totals.set(
          period,
          covers.map(() => 0),
        );
        order.push(period);
      }
      const accumulated = totals.get(period);
      covers.forEach((cover, index) => {
        const value = Number(row[index + 1]);
        if (Number.isFinite(value)) accumulated[index] += value;
      });
    });
  });

  return {
    columns: [text.columns.period, ...covers],
    rows: order.map((period) => [period, ...totals.get(period).map((value) => value.toFixed(2))]),
  };
};

/**
 * @function buildLandCoverReport
 * @description The whole document, as data.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring.
 * @param {Array<Array<Number>>} [params.baselineCoords] - Baseline vertices.
 * @param {Array<Object>} [params.transectsData] - The transects drawn along the baseline.
 * @param {Object} [params.transectsAttributes] - `transectsSqueeze`, keyed by transect number.
 *  CSqueeze produces these; EUSCoast does not classify squeeze, so it passes nothing and the
 *  classification sections are skipped rather than printed empty.
 * @param {Object} [params.transectsCalcs] - `transectsCalcs`, the land cover areas per period.
 * @param {Object} [params.periodLabels] - The periods the land cover was read over.
 * @param {String} [params.collection] - Which land cover collection was used.
 * @param {String} [params.kmlFileName] - The uploaded area file.
 * @param {Array<Object>} [params.images] - Already loaded by {@link loadImageForReport}.
 * @param {Object} params.text - Every piece of wording, already translated. `text.title` is what
 *  distinguishes a CSqueeze report from an EUSCoast one.
 * **Called by:
 * @see {ProcessingPage} - CSqueeze's and EUSCoast's result pages.
 * @memberof module:LAND_COVER_PDF_REPORT
 * @returns {Object} A document description for {@link renderReport}.
 */
export const buildLandCoverReport = ({
  coordinates,
  baselineCoords,
  transectsData,
  transectsAttributes,
  transectsCalcs,
  periodLabels,
  collection,
  kmlFileName,
  images,
  text,
}) => {
  const entries = squeezeEntries(transectsAttributes);
  const periods = Object.values(periodLabels || {}).filter(Boolean);
  const forecast = entries.find((entry) => Number.isFinite(Number(entry.forecast_years)))?.forecast_years;

  return {
    title: text.title,
    subtitle: text.subtitle,
    footer: text.footer,
    missingImageNote: text.missingImage,
    sections: [
      {
        heading: text.areaHeading,
        rows: [
          [text.areaFile, kmlFileName],
          [text.areaExtent, boundingBox(coordinates)],
          [text.baselineVertices, baselineCoords?.length],
          [text.transectCount, transectsData?.length || null],
        ],
      },
      {
        heading: text.sourceHeading,
        rows: [
          [text.collection, collection],
          [text.periodCount, periods.length || null],
          [text.periodList, periods.length ? periods.join(', ') : null],
          [text.forecast, Number.isFinite(Number(forecast)) ? text.forecastYears({ years: forecast }) : null],
        ],
      },
      {
        heading: text.coverHeading,
        table: landCoverTotalsTable(transectsCalcs, text.cover),
        note: Object.keys(transectsCalcs || {}).length ? text.coverNote : null,
      },
      {
        heading: text.classHeading,
        table: squeezeClassTable(entries, text.classes),
        note: entries.length ? text.classNote : null,
      },
      {
        heading: text.transectHeading,
        table: squeezeTransectTable(entries, text.transects),
        note: entries.length
          ? entries.length > TRANSECT_TABLE_LIMIT
            ? text.transectTruncated({ shown: TRANSECT_TABLE_LIMIT, total: entries.length })
            : text.transectComplete({ total: entries.length })
          : null,
      },
      { heading: text.mapsHeading, images: images || [] },
    ],
  };
};
